import { z } from "zod";

/**
 * Espelha exatamente `storage.buckets` da migration v14 e as policies de
 * `storage.objects`. O banco continua sendo a autoridade: isto evita gastar
 * rede com um envio que já sabemos recusado e mantém uma descrição única dos
 * quatro buckets privados para React e Worker.
 */
export const storageBucketSchema = z.enum([
  "exames",
  "report-branding",
  "body-progress",
  "report-exports",
]);

export type StorageBucket = z.infer<typeof storageBucketSchema>;

export interface StorageBucketPolicy {
  /** Nenhum bucket é público; a leitura sempre passa por URL assinada. */
  readonly isPublic: false;
  readonly maxBytes: number;
  readonly mimeTypes: readonly string[];
  /**
   * Quantos segmentos UUID precedem o nome do arquivo. O primeiro é sempre
   * `auth.uid()`; o segundo, quando existe, é o perfil validado em `alunos`.
   */
  readonly uuidSegments: number;
  /** Validade máxima da URL assinada, em segundos. */
  readonly signedUrlSeconds: number;
}

export const STORAGE_BUCKETS: Readonly<Record<StorageBucket, StorageBucketPolicy>> = Object.freeze({
  exames: {
    isPublic: false,
    maxBytes: 4 * 1024 * 1024,
    mimeTypes: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
    uuidSegments: 2,
    signedUrlSeconds: 60,
  },
  "report-branding": {
    isPublic: false,
    maxBytes: 2 * 1024 * 1024,
    mimeTypes: ["image/png", "image/jpeg", "image/webp"],
    uuidSegments: 1,
    signedUrlSeconds: 300,
  },
  "body-progress": {
    isPublic: false,
    maxBytes: 8 * 1024 * 1024,
    mimeTypes: ["image/jpeg", "image/png", "image/webp"],
    uuidSegments: 3,
    signedUrlSeconds: 300,
  },
  "report-exports": {
    isPublic: false,
    maxBytes: 20 * 1024 * 1024,
    mimeTypes: ["application/pdf"],
    uuidSegments: 2,
    signedUrlSeconds: 300,
  },
});

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const OBJECT_NAME = /^[\p{L}\p{N}][\p{L}\p{N}._-]{0,159}$/u;

export interface StoragePath {
  readonly bucket: StorageBucket;
  readonly segments: readonly string[];
  readonly objectName: string;
}

/** Nome de objeto previsível: sem separador, sem `..` e sem caractere de controle. */
export function safeObjectName(name: string): string {
  const cleaned = name
    .normalize("NFC")
    .replace(/[^\p{L}\p{N}._-]+/gu, "-")
    .replace(/^[.-]+/u, "")
    .slice(-120);
  return cleaned === "" ? "arquivo" : cleaned;
}

/**
 * Monta `uuid/[uuid…]/nome`. Recusa qualquer segmento que não seja UUID e
 * qualquer nome capaz de escapar da pasta da conta.
 */
export function buildStoragePath(
  bucket: StorageBucket,
  segments: readonly string[],
  objectName: string,
): string {
  const policy = STORAGE_BUCKETS[bucket];
  if (segments.length !== policy.uuidSegments) {
    throw new Error(`path do bucket ${bucket} exige ${String(policy.uuidSegments)} segmentos`);
  }
  for (const segment of segments) {
    if (!UUID.test(segment)) throw new Error(`segmento de path invalido no bucket ${bucket}`);
  }
  const safe = safeObjectName(objectName);
  if (!OBJECT_NAME.test(safe)) throw new Error("nome de objeto invalido");
  return [...segments, safe].join("/");
}

/** `null` para qualquer path malformado — inclusive `..`, `//` e segmento não-UUID. */
export function parseStoragePath(bucket: StorageBucket, path: string): StoragePath | null {
  const policy = STORAGE_BUCKETS[bucket];
  if (path === "" || path.includes("//") || path.startsWith("/") || path.endsWith("/")) return null;
  const parts = path.split("/");
  if (parts.length !== policy.uuidSegments + 1) return null;
  const segments = parts.slice(0, policy.uuidSegments);
  const objectName = parts[policy.uuidSegments] ?? "";
  if (!segments.every((segment) => UUID.test(segment))) return null;
  if (!OBJECT_NAME.test(objectName)) return null;
  return { bucket, segments, objectName };
}

/** O path pertence a esta conta e, quando o bucket exige, a este perfil. */
export function pathBelongsTo(
  bucket: StorageBucket,
  path: string,
  userId: string,
  profileId?: string,
): boolean {
  const parsed = parseStoragePath(bucket, path);
  if (parsed === null) return false;
  if (parsed.segments[0]?.toLowerCase() !== userId.toLowerCase()) return false;
  if (profileId === undefined || STORAGE_BUCKETS[bucket].uuidSegments < 2) return true;
  return parsed.segments[1]?.toLowerCase() === profileId.toLowerCase();
}

export function signedUrlSeconds(bucket: StorageBucket, requested?: number | null): number {
  const maximum = STORAGE_BUCKETS[bucket].signedUrlSeconds;
  if (typeof requested !== "number" || !Number.isFinite(requested) || requested <= 0)
    return maximum;
  return Math.min(Math.trunc(requested), maximum);
}

export const uploadContractSchema = z
  .object({
    bucket: storageBucketSchema,
    path: z.string().min(3).max(700),
    contentType: z.string().min(3).max(120),
    sizeBytes: z.number().int().positive(),
  })
  .strict()
  .superRefine((input, context) => {
    const policy = STORAGE_BUCKETS[input.bucket];
    if (parseStoragePath(input.bucket, input.path) === null) {
      context.addIssue({
        code: "custom",
        message: "path fora do formato do bucket",
        path: ["path"],
      });
    }
    if (!policy.mimeTypes.includes(input.contentType)) {
      context.addIssue({
        code: "custom",
        message: "tipo nao aceito pelo bucket",
        path: ["contentType"],
      });
    }
    if (input.sizeBytes > policy.maxBytes) {
      context.addIssue({
        code: "custom",
        message: "tamanho acima do limite do bucket",
        path: ["sizeBytes"],
      });
    }
  });

export type UploadContract = z.infer<typeof uploadContractSchema>;
